import { StringParam, withDefault } from 'use-query-params';
import {
  CustomStringParam,
  FilterType,
  SchemaFilterType,
  SortDir,
} from '@ui/src';
import { ApplicationType, NewApplicationStatus } from '../types';

export const newApplicationListColumns = [
  {
    name: 'id',
    label: 'Doc N.',
    hidden: false,
  },
  {
    name: 'createdAt',
    label: 'Date subm.',
    hidden: false,
    sortable: true,
  },
  {
    name: 'firstName',
    label: 'First Name',
    hidden: false,
    sortable: true,
  },
  {
    name: 'lastName',
    label: 'Last Name',
    hidden: false,
    sortable: true,
  },
  {
    name: 'companyName',
    label: 'Company Name',
    hidden: false,
    sortable: true,
  },
  {
    name: 'reviewEmployeeId',
    label: 'Responsible',
    hidden: false,
    sortable: false,
  },
  {
    name: 'email',
    label: 'Email',
    hidden: false,
    sortable: true,
  },
  {
    name: 'phone',
    label: 'Phone',
    hidden: false,
    sortable: true,
  },
  {
    name: 'countryId',
    label: 'Country',
    filterModalPlacement: 'right',
    hidden: false,
  },
  {
    name: 'type',
    label: 'Type',
    filterModalPlacement: 'right',
    hidden: false,
    sortable: true,
  },
  {
    name: 'status',
    label: 'Status',
    filterModalPlacement: 'right',
    hidden: false,
    sortable: true,
  },
];

export const newApplicationListColumnsLabels = {
  firstName: 'First Name',
  lastName: 'Last Name',
  companyName: 'Company Name',
  phone: 'Phone',
  email: 'Email',
  type: 'Type',
  status: 'Status',
  countryId: 'Country',
  createdAt: 'Date subm.',
};

export const newApplicationListDefaultSorter = {
  sortProp: newApplicationListColumns[1].name,
  sortDir: SortDir.DESC,
};

export const newApplicationListSchema = {
  firstName: {
    value: CustomStringParam,
    type: SchemaFilterType.STRING,
  },
  lastName: {
    value: CustomStringParam,
    type: SchemaFilterType.STRING,
  },
  type: {
    value: CustomStringParam,
    type: SchemaFilterType.STRING,
  },
  status: {
    value: CustomStringParam,
    type: SchemaFilterType.STRING,
  },
  companyName: {
    value: CustomStringParam,
    type: SchemaFilterType.STRING,
  },
  phone: {
    value: CustomStringParam,
    type: SchemaFilterType.STRING,
  },
  email: {
    value: CustomStringParam,
    type: SchemaFilterType.STRING,
  },
  countryId: {
    value: CustomStringParam,
    type: SchemaFilterType.STRING,
  },
  createdAt: {
    value: CustomStringParam,
    type: SchemaFilterType.DATE_RANGE,
    queryNames: ['createdDateFrom', 'createdDateTo'],
  },
  createdDateFrom: {
    value: CustomStringParam,
    type: SchemaFilterType.DATE_RANGE,
  },
  createdDateTo: {
    value: CustomStringParam,
    type: SchemaFilterType.DATE_RANGE,
  },
  sortProp: {
    value: withDefault(StringParam, newApplicationListDefaultSorter.sortProp),
    type: SchemaFilterType.STRING,
  },
  sortDir: {
    value: withDefault(StringParam, newApplicationListDefaultSorter.sortDir),
    type: SchemaFilterType.STRING,
  },
};

export const newApplicationListFiltersSchema = [
  {
    name: 'firstName',
    type: FilterType.TEXT,
    placeholder: 'Search by name...',
  },
  {
    name: 'lastName',
    type: FilterType.TEXT,
    placeholder: 'Search by name...',
  },
  {
    name: 'companyName',
    type: FilterType.TEXT,
    placeholder: 'Search by company name...',
  },
  {
    name: 'phone',
    type: FilterType.TEXT,
    placeholder: 'Search by phone...',
  },
  {
    name: 'email',
    type: FilterType.TEXT,
    placeholder: 'Search by email...',
  },
  {
    name: 'createdAt',
    type: FilterType.DATE_RANGE,
  },
  {
    name: 'countryId',
    type: FilterType.AUTOCOMPLETE_MULTIPLE,
    placeholder: 'Enter country',
  },
  {
    name: 'type',
    type: FilterType.CHECKBOX,
    fields: [
      {
        name: 'type',
        title: 'Individual',
        value: ApplicationType.INDIVIDUAL,
      },
      {
        name: 'type',
        title: 'Corporate',
        value: ApplicationType.CORPORATE,
      },
      {
        name: 'type',
        title: 'Company officials',
        value: ApplicationType.COMPANY_OFFICIAL,
      },
    ],
  },
  {
    name: 'status',
    type: FilterType.CHECKBOX,
    fields: [
      {
        name: 'status',
        title: 'Incomplete',
        value: NewApplicationStatus.INCOMPLETE,
      },
      {
        name: 'status',
        title: 'Accepted',
        value: NewApplicationStatus.ACCEPTED,
      },
      {
        name: 'status',
        title: 'Waiting for review',
        value: NewApplicationStatus.READY_TO_REVIEW,
      },
      {
        name: 'status',
        title: 'Review in progress',
        value: NewApplicationStatus.REVIEW_IN_PROGRESS,
      },
      {
        name: 'status',
        title: 'Declined',
        value: NewApplicationStatus.DECLINED,
      },
    ],
  },
];

export const auditLogsSchema = {
  action: {
    value: CustomStringParam,
    type: SchemaFilterType.STRING,
  },
  result: {
    value: CustomStringParam,
    type: SchemaFilterType.STRING,
  },
  ipSource: {
    value: CustomStringParam,
    type: SchemaFilterType.STRING,
  },
  eventCreatedAt: {
    value: CustomStringParam,
    type: SchemaFilterType.DATE_RANGE,
    queryNames: ['eventCreatedAtFrom', 'eventCreatedAtTo'],
  },
  eventCreatedAtFrom: {
    value: CustomStringParam,
    type: SchemaFilterType.DATE_RANGE,
  },
  eventCreatedAtTo: {
    value: CustomStringParam,
    type: SchemaFilterType.DATE_RANGE,
  },
};

export const auditLogsFiltersSchema = [
  {
    name: 'result',
    type: FilterType.RADIO,
    fields: [
      {
        name: 'result',
        title: 'Successful',
        value: 'SUCCESSFUL',
      },
      {
        name: 'result',
        title: 'Failed',
        value: 'FAILED',
      },
    ],
  },
  {
    name: 'ipSource',
    type: FilterType.TEXT,
    placeholder: 'Enter IP...',
  },
  {
    name: 'eventCreatedAt',
    type: FilterType.DATE_RANGE,
    withoutCustomSelector: true,
  },
  {
    name: 'action',
    type: FilterType.AUTOCOMPLETE_MULTIPLE,
    placeholder: 'Enter action...',
  },
];

export const auditLogsColumns = [
  {
    name: 'id',
    label: '',
    hidden: true,
  },
  {
    name: 'action',
    label: 'Action',
    sortable: false,
  },
  {
    name: 'result',
    label: 'Result',
    sortable: false,
  },
  {
    name: 'ipSource',
    label: 'IP',
    sortable: false,
  },
  {
    name: 'eventCreatedAt',
    label: 'Creation Date',
    sortable: false,
    filterModalPlacement: 'right',
  },
];

export const auditLogsColumnsLabels = {
  action: 'Action',
  result: 'Result',
  eventCreatedAt: 'Created Date',
  eventCreatedAtFrom: 'Created Date From',
  eventCreatedAtTo: 'Created Date To',
  ipSource: 'IP',
};

export const locksSchema = {
  locked: {
    value: CustomStringParam,
    type: SchemaFilterType.STRING,
  },
  reason: {
    value: CustomStringParam,
    type: SchemaFilterType.STRING,
  },
  type: {
    value: CustomStringParam,
    type: SchemaFilterType.STRING,
  },
  createdByEmployeeId: {
    value: CustomStringParam,
    type: SchemaFilterType.STRING,
  },
  createdAt: {
    value: CustomStringParam,
    type: SchemaFilterType.DATE_RANGE,
    queryNames: ['createdAtFrom', 'createdAtTo'],
  },
  createdAtFrom: {
    value: CustomStringParam,
    type: SchemaFilterType.DATE_RANGE,
  },
  createdAtTo: {
    value: CustomStringParam,
    type: SchemaFilterType.DATE_RANGE,
  },
  expiresAt: {
    value: CustomStringParam,
    type: SchemaFilterType.DATE_RANGE,
    queryNames: ['expiresAtFrom', 'expiresAtTo'],
  },
  expiresAtFrom: {
    value: CustomStringParam,
    type: SchemaFilterType.DATE_RANGE,
  },
  expiresAtTo: {
    value: CustomStringParam,
    type: SchemaFilterType.DATE_RANGE,
  },
};

export const locksFiltersSchema = [
  {
    name: 'type',
    type: FilterType.RADIO,
    withClearButton: true,
    fields: [
      {
        name: 'type',
        title: 'Auto',
        value: 'AUTO',
      },
      {
        name: 'type',
        title: 'by Employee',
        value: 'MANUAL',
      },
    ],
  },
  {
    name: 'locked',
    type: FilterType.RADIO,
    withClearButton: true,
    fields: [
      {
        name: 'locked',
        title: 'Yes',
        value: 'true',
      },
      {
        name: 'locked',
        title: 'No',
        value: 'false',
      },
    ],
  },
  {
    name: 'createdByEmployeeId',
    type: FilterType.AUTOCOMPLETE,
    placeholder: 'Search by Employee...',
  },
  {
    name: 'createdAt',
    type: FilterType.DATE_RANGE,
    withoutCustomSelector: true,
  },
  {
    name: 'expiresAt',
    type: FilterType.DATE_RANGE,
    withoutCustomSelector: true,
  },
  {
    name: 'reason',
    type: FilterType.AUTOCOMPLETE_MULTIPLE,
    placeholder: 'Enter reason...',
  },
];

export const locksColumns = [
  {
    name: 'id',
    label: '',
    hidden: true,
  },
  {
    name: 'locked',
    label: 'Locked',
    sortable: false,
  },
  {
    name: 'createdAt',
    label: 'Created Date',
    sortable: false,
  },
  {
    name: 'expiresAt',
    label: 'Expires date',
    sortable: false,
  },
  {
    name: 'reason',
    label: 'Reason',
    sortable: false,
  },
  {
    name: 'createdByEmployeeId',
    label: 'Created by Employee',
    sortable: false,
  },
  {
    name: 'type',
    label: 'Creation type',
    sortable: false,
  },
  {
    name: 'createdByComment',
    label: 'Comment',
    sortable: false,
  },
];

export const locksColumnsLabels = {
  locked: 'Locked',
  reason: 'Reason',
  type: 'Creation type',
  createdByEmployeeId: 'Created by Employee',
  createdAt: 'Created Date',
  createdAtFrom: 'Created Date From',
  createdAtTo: 'Created Date To',
  expiresAt: 'Expires Date',
  expiresAtFrom: 'Expires Date From',
  expiresAtTo: 'Expires Date To',
};

export const logsSchema = {
  action: {
    value: CustomStringParam,
    type: SchemaFilterType.STRING,
  },
  ipSource: {
    value: CustomStringParam,
    type: SchemaFilterType.STRING,
  },
  result: {
    value: CustomStringParam,
    type: SchemaFilterType.STRING,
  },
  eventCreatedAt: {
    value: CustomStringParam,
    type: SchemaFilterType.DATE_RANGE,
    queryNames: ['eventCreatedAtFrom', 'eventCreatedAtTo'],
  },
  eventCreatedAtFrom: {
    value: CustomStringParam,
    type: SchemaFilterType.DATE_RANGE,
  },
  eventCreatedAtTo: {
    value: CustomStringParam,
    type: SchemaFilterType.DATE_RANGE,
  },
};

export const filtersSchema = [
  {
    name: 'result',
    type: FilterType.RADIO,
    fields: [
      {
        name: 'result',
        title: 'Successful',
        value: 'SUCCESSFUL',
      },
      {
        name: 'result',
        title: 'Failed',
        value: 'FAILED',
      },
    ],
  },
  {
    name: 'ipSource',
    type: FilterType.TEXT,
    placeholder: 'Enter IP...',
  },
  {
    name: 'eventCreatedAt',
    type: FilterType.DATE_RANGE,
    withoutCustomSelector: true,
  },
  {
    name: 'action',
    type: FilterType.AUTOCOMPLETE_MULTIPLE,
    placeholder: 'Enter action...',
  },
];

export const columns = [
  {
    name: 'id',
    label: '',
    hidden: true,
  },
  {
    name: 'action',
    label: 'Action',
    sortable: false,
  },
  {
    name: 'result',
    label: 'Result',
    sortable: false,
  },
  {
    name: 'ipSource',
    label: 'IP',
    sortable: false,
  },
  {
    name: 'eventCreatedAt',
    label: 'Creation Date',
    sortable: false,
    filterModalPlacement: 'right',
  },
];

export const columnsLabels = {
  action: 'Action',
  result: 'Result',
  ipSource: 'IP',
  eventCreatedAt: 'Creation Date',
};
